// Tabla comparativa expandible de los 3 tiers, side-by-side por feature.
// Se renderiza debajo de los 3 PricingCard en PricingSection.
// Toggle "Ver todas las features" abre/cierra.

import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Check, X, ChevronDown } from 'lucide-react';
import { useBookDemoStore } from '../store/book-demo-store';

type Cell = boolean | string;

interface FeatureRow {
  group: string;
  features: { label: string; starter: Cell; pro: Cell; enterprise: Cell }[];
}

const COMPARE_ROWS: FeatureRow[] = [
  {
    group: 'Capacidad',
    features: [
      { label: 'Usuarios incluidos', starter: '5', pro: '15', enterprise: 'A medida' },
      { label: 'Cotizaciones', starter: 'Ilimitadas', pro: 'Ilimitadas', enterprise: 'Ilimitadas' },
      { label: 'Modelos de pricing', starter: '3', pro: 'Ilimitados', enterprise: 'Ilimitados' },
      { label: 'Multi-sucursal', starter: false, pro: false, enterprise: true },
    ],
  },
  {
    group: 'Pricing & Cotización',
    features: [
      { label: 'Catálogo de productos', starter: true, pro: true, enterprise: true },
      { label: 'Multi-moneda', starter: true, pro: true, enterprise: true },
      { label: 'PDF con branding', starter: true, pro: true, enterprise: true },
      { label: 'Workflow de aprobaciones', starter: false, pro: true, enterprise: true },
      { label: 'Pricing dinámico', starter: false, pro: 'Básico', enterprise: 'Avanzado' },
      { label: 'Reglas automáticas de precio', starter: false, pro: true, enterprise: true },
    ],
  },
  {
    group: 'Analytics & IA',
    features: [
      {
        label: 'Asistente IA (Claude)',
        starter: 'Básico',
        pro: 'Completo',
        enterprise: 'Personalizado',
      },
      { label: 'Analytics avanzado', starter: false, pro: true, enterprise: true },
      { label: 'Detección de anomalías', starter: false, pro: true, enterprise: true },
    ],
  },
  {
    group: 'Integraciones & API',
    features: [
      { label: 'API REST', starter: false, pro: true, enterprise: true },
      { label: 'Webhooks', starter: false, pro: false, enterprise: true },
      {
        label: 'Conectores nativos (SAP/Salesforce/Shopify…)',
        starter: false,
        pro: true,
        enterprise: true,
      },
    ],
  },
  {
    group: 'Soporte',
    features: [
      { label: 'Soporte por email', starter: true, pro: true, enterprise: true },
      { label: 'Soporte prioritario', starter: false, pro: true, enterprise: true },
      { label: 'Soporte 24/7 + SLA 99.5%', starter: false, pro: false, enterprise: true },
      { label: 'Capacitación presencial', starter: false, pro: false, enterprise: true },
      { label: 'Account manager dedicado', starter: false, pro: false, enterprise: true },
    ],
  },
];

function CellValue({ value }: { value: Cell }) {
  if (value === true) {
    return (
      <div className="flex items-center justify-center">
        <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex items-center justify-center">
        <X className="w-4 h-4 text-gray-300 dark:text-gray-700" />
      </div>
    );
  }
  return (
    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 text-center block">
      {value}
    </span>
  );
}

export function PricingComparisonTable() {
  const [open, setOpen] = useState(false);
  const openDemo = useBookDemoStore((s) => s.open);

  return (
    <div className="mt-12 max-w-5xl mx-auto">
      <div className="text-center mb-6">
        <button
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:border-blue-300 dark:hover:border-blue-500/40 hover:bg-blue-50/50 dark:hover:bg-blue-500/5 transition-all"
          aria-expanded={open}
        >
          {open ? 'Ocultar comparativa completa' : 'Ver comparativa completa de features'}
          <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="w-4 h-4" />
          </motion.span>
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 dark:backdrop-blur-sm overflow-hidden shadow-sm">
              {/* Sticky header */}
              <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-2 px-5 py-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-900/60 border-b border-gray-200 dark:border-gray-800">
                <div></div>
                <div className="text-center">
                  <p className="text-sm font-bold text-gray-900 dark:text-white">Starter</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    A medida
                  </p>
                </div>
                <div className="text-center relative">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow">
                      Más elegido
                    </span>
                  </div>
                  <p className="text-sm font-bold text-blue-700 dark:text-blue-400 mt-1.5">Pro</p>
                  <p className="text-[10px] text-blue-500/70 dark:text-blue-500/70 uppercase tracking-wider">
                    A medida
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-gray-900 dark:text-white">Enterprise</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    A medida
                  </p>
                </div>
              </div>

              {/* Groups */}
              {COMPARE_ROWS.map((group) => (
                <div key={group.group}>
                  <div className="px-5 py-2.5 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-800">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {group.group}
                    </p>
                  </div>
                  {group.features.map((f, i) => (
                    <div
                      key={f.label}
                      className={`grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-2 px-5 py-3 items-center border-b border-gray-50 dark:border-gray-800/50 ${
                        i % 2 === 1 ? 'bg-gray-50/30 dark:bg-gray-900/20' : ''
                      }`}
                    >
                      <p className="text-sm text-gray-700 dark:text-gray-300">{f.label}</p>
                      <CellValue value={f.starter} />
                      <div className="bg-blue-50/50 dark:bg-blue-500/5 rounded py-2">
                        <CellValue value={f.pro} />
                      </div>
                      <CellValue value={f.enterprise} />
                    </div>
                  ))}
                </div>
              ))}

              {/* Footer CTA */}
              <div className="px-5 py-5 bg-gradient-to-r from-blue-50 via-violet-50 to-blue-50 dark:from-blue-950/30 dark:via-violet-950/30 dark:to-blue-950/30 border-t border-gray-200 dark:border-gray-800 text-center">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                  ¿Necesitás algo distinto? Lo armamos juntos en la demo.
                </p>
                <button
                  onClick={() => openDemo()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-violet-600 text-white text-sm font-semibold hover:shadow-lg hover:shadow-blue-600/25 transition-all"
                >
                  Solicitar demo personalizada
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
